import {
    IIntegrationResponse,
    IntegrationCreateRequest,
    IntegrationType,
    IntegrationUpdateRequest,
} from '@kcftech/prometheustypescriptapiclient';
import { sdClient } from 'contexts/SDKClientContext';
import {
    AuthenticationType,
    IntegrationDetails,
    IntegrationDetailsResponse,
    IntegrationState,
} from 'pages/Settings/Integrations/types';
import { fetchGatewayWithAuth } from 'utils/fetch';
import { HttpMethod } from 'utils/types';

export type GetIntegrationConfigurationRequest = Readonly<{
    locationId: string;
    integrationType: IntegrationType;
    state?: IntegrationState;
}>;

export type CreateIntegrationProps = Readonly<{
    locationId: string;
    data: IntegrationCreateRequest;
    configuration: IntegrationDetails;
}>;

export type UpdateIntegrationProps = Readonly<{
    locationId: string;
    integration: IIntegrationResponse;
    updatedIntegration?: IntegrationUpdateRequest;
    updatedConfiguration?: IntegrationDetails;
}>;

export type DeleteIntegrationProps = Readonly<{
    locationId: string;
    integration: IIntegrationResponse;
}>;

export type ConfigureIntegrationProps = Readonly<{
    locationId: string;
    systemId?: string;
    method: HttpMethod.Get | HttpMethod.Post | HttpMethod.Patch | HttpMethod.Delete;
    integrationType: IntegrationType;
    payload?: IntegrationDetails;
    state?: IntegrationState;
    ignoreErrors?: boolean;
}>;

/* Note: Slash and quote characters will be interpreted by integrations APIs as path characters, so they are replaced with underscores.
    This does not impact the UI, which will display as the user has entered it.
    TODO: The character list is not expansive, and only covers commonly used problematic characters.
    - @avopelius
 */
const configuredIntegrationSystemId = (integration: IIntegrationResponse) =>
    `${integration.SoftwareName.replaceAll(/[\\/'"]/g, '_')}-${integration.Id}`;

async function requestIntegrationConfiguration({
    locationId,
    systemId,
    method,
    integrationType,
    payload,
    state,
    ignoreErrors,
}: ConfigureIntegrationProps): Promise<Response> {
    const url = `v4/${integrationType.toLowerCase()}/configurations/${locationId}${
        systemId ? `/${systemId}` : ''
    }${state ? `?state=${state}` : ''}`;

    return fetchGatewayWithAuth({
        path: url,
        method,
        payload,
        ignoreErrors,
    });
}

export async function getLocationConfigurations(
    requestOptions: GetIntegrationConfigurationRequest
): Promise<IntegrationDetails[] | IntegrationDetailsResponse> {
    const response = await requestIntegrationConfiguration({
        locationId: requestOptions.locationId,
        method: HttpMethod.Get,
        integrationType: requestOptions.integrationType,
        state: requestOptions.state,
    });
    if (response.status === 404) {
        throw new Error('Location not configured');
    }
    return response.json();
}

export async function createIntegration({
    locationId,
    data,
    configuration,
}: CreateIntegrationProps) {
    const createdIntegration = await sdClient.createIntegrationForLocation({
        id: locationId,
        body: data,
    });
    try {
        const payload: IntegrationDetails = {
            ...configuration,
            systemid: configuredIntegrationSystemId(createdIntegration),
        };
        await requestIntegrationConfiguration({
            locationId,
            systemId: configuredIntegrationSystemId(createdIntegration),
            method: HttpMethod.Post,
            integrationType: data.IntegrationType,
            payload,
            state: IntegrationState.Inactive,
        });
        return undefined;
    } catch {
        await sdClient.deleteIntegrationForLocation({
            id: locationId,
            integrationId: createdIntegration.Id,
        });
        throw new Error('Unable to create integration');
    }
}

export async function updateIntegration({
    locationId,
    integration,
    updatedIntegration,
    updatedConfiguration,
}: UpdateIntegrationProps) {
    if (!updatedIntegration && !updatedConfiguration) {
        throw new Error('No updates provided');
    }
    if (updatedIntegration) {
        const payload = new IntegrationUpdateRequest({
            SoftwareName: integration.SoftwareName,
            Notes: updatedIntegration.Notes ?? integration.Notes,
            Active: updatedIntegration.Active ?? integration.Active,
        });
        await sdClient.updateIntegrationForLocation({
            id: locationId,
            integrationId: integration.Id,
            body: payload,
        });
    }
    if (updatedConfiguration || updatedIntegration?.Active !== integration.Active) {
        const updatedState = (): IntegrationState | undefined => {
            if (!updatedIntegration) {
                return integration.Active
                    ? IntegrationState.Active
                    : IntegrationState.Inactive;
            }
            return updatedIntegration.Active
                ? IntegrationState.Active
                : IntegrationState.Inactive;
        };
        await requestIntegrationConfiguration({
            locationId,
            systemId: configuredIntegrationSystemId(integration),
            method: HttpMethod.Patch,
            integrationType: integration.IntegrationType,
            payload: {
                ...updatedConfiguration,
                systemid: configuredIntegrationSystemId(integration),
            },
            state: updatedState(),
        });
    }
}

export async function deleteIntegration({
    locationId,
    integration,
}: DeleteIntegrationProps) {
    await sdClient.deleteIntegrationForLocation({
        id: locationId,
        integrationId: integration.Id,
    });
    await requestIntegrationConfiguration({
        locationId,
        systemId: configuredIntegrationSystemId(integration),
        method: HttpMethod.Delete,
        integrationType: integration.IntegrationType,
    });
}

export async function getIntegrationDetails(
    requestOptions: GetIntegrationConfigurationRequest,
    integration: IIntegrationResponse
): Promise<IntegrationDetails> {
    const response = await requestIntegrationConfiguration({
        locationId: requestOptions.locationId,
        systemId: configuredIntegrationSystemId(integration),
        method: HttpMethod.Get,
        integrationType: requestOptions.integrationType,
    });
    const { data } = await response.json();
    return data;
}

export const hasConnectionDetails = (
    integrationSettings: IntegrationDetails
): boolean => {
    switch (integrationSettings.authtype) {
        case AuthenticationType.Basic:
            return (
                integrationSettings.base_url !== undefined &&
                integrationSettings.secret !== undefined &&
                integrationSettings.id !== undefined
            );
        case AuthenticationType.OAuth:
            return (
                integrationSettings.base_url !== undefined &&
                integrationSettings.secret !== undefined &&
                integrationSettings.tokenurl !== undefined &&
                integrationSettings.id !== undefined
            );
        case AuthenticationType.ApiKey:
            return (
                integrationSettings.base_url !== undefined &&
                integrationSettings.secret !== undefined
            );
        default:
            return false;
    }
};
